import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import translations from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import NoticePopover from '@components/NoticePopover'
import { CssTruncation } from '@components/TruncateLines/index.styles'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { preparePendoSavedSearchEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoEventType } from '@modules/pendo/types'
import { SavedSearch, SavedSearchAlertsFrequency } from '@modules/savedSearch/types'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { Box, Button, FormControl, MenuItem, Select, Switch, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useNotification } from '@shared/hooks/useNotification'
import useSelection from '@shared/hooks/useSelection'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { colors } from '@theme/variables'

import savedSearchApi from '../api'
import { NewCountBadge } from './index.styles'

const SavedSearchesOverview = ({
  close,
  onSearchSelected,
  resetSearchName,
  appliedSavedSearchName,
}: {
  close: () => void
  onSearchSelected?: (savedSearch: Nullable<SavedSearch>) => void
  resetSearchName: () => void
  appliedSavedSearchName?: string
}) => {
  const { user } = useAuthContext()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { showNotification } = useNotification()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { hasAccessToSavedSearch } = useFeatureFlags()
  const navigate = useNavigate()
  const location = useLocation()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const deleteButtonRef = useRef(null)
  const [saveSearchNoticeAnchor, setSaveSearchNoticeAnchor] = useState<null | { anchor: HTMLElement; text: string }>(null)

  const { mutate: deleteSavedSearches } = useMutation({
    mutationFn: savedSearchApi.deleteSavedSearches.fn,
    onSuccess: () => {
      queryClient.setQueryData<SavedSearch[]>([savedSearchApi.getSavedSearches.key], oldData => {
        const newData = oldData?.filter(item => !selectedIds.includes(item.id)) || []

        if (!newData?.length) {
          close()
        }

        return newData
      })
    },
  })

  const { data: savedSearches } = useQuery({
    queryKey: [savedSearchApi.getSavedSearches.key],
    queryFn: () => savedSearchApi.getSavedSearches.fn(),
    enabled: hasAccessToSavedSearch,
    refetchOnMount: 'always',
  })

  const { mutate: updateSavedSearchByName } = useMutation({
    mutationFn: savedSearchApi.updateSavedSearchByName.fn,
    onSuccess: async (_: unknown, resultItem: SavedSearch) => {
      queryClient.setQueryData<SavedSearch[]>([savedSearchApi.getSavedSearches.key], oldData => {
        return oldData?.map(item => (item.id === resultItem.id ? resultItem : item)) || []
      })
    },
    onError: (error?: { response?: { data?: { message?: string } } }) => {
      if (error?.response?.data?.message?.startsWith('You have reached your limit')) {
        showNotification('You have reached your limit of 25 email notifications for saved searches.', 'error')
      } else {
        showNotification('Something went wrong', 'error')
      }
    },
  })

  const { selectedIds, toggleSelectItem, toggleSelectAll, clearSelectedItems } = useSelection(savedSearches?.map(i => i.id) ?? [])

  const handleRunSearch = (savedSearch: SavedSearch) => {
    navigate(`${location.pathname}${savedSearch.searchQuery}`, { replace: true })
    close()
    onSearchSelected?.(savedSearch)

    const pendoEvent = preparePendoSavedSearchEvent({
      savedSearchId: savedSearch.id,
      eventType: 'run',
      user,
    })

    trackPendoEvent(PendoEventType.SavedSearchRun, pendoEvent)
  }

  const handleDeleteClick = () => {
    if (!selectedIds.length && deleteButtonRef.current) {
      setSaveSearchNoticeAnchor({
        anchor: deleteButtonRef.current,
        text: translations.SAVE_SEARCH_PARAMS,
      })
    } else {
      setShowDeleteConfirmation(true)
    }
  }

  const handleConfirmDelete = () => {
    const deleteItems = savedSearches?.filter(i => selectedIds.includes(i.id))

    deleteSavedSearches(
      selectedIds.map(id => id.toString()),
      {
        onSuccess: () => {
          clearSelectedItems()
          const names = deleteItems?.map(i => i.name)

          if (names?.includes(appliedSavedSearchName ?? '')) {
            resetSearchName()
          }
          deleteItems?.forEach(item => {
            const pendoEvent = preparePendoSavedSearchEvent({
              savedSearchId: item.id,
              eventType: 'delete',
              user,
            })

            trackPendoEvent(PendoEventType.SavedSearchDelete, pendoEvent)
          })
        },
      },
    )
    setShowDeleteConfirmation(false)
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false)
  }

  return (
    <Box p="20px" maxHeight="550px" maxWidth="720px" overflow="hidden" border={`1px solid ${colors.primary}`}>
      <Box ml="13px">
        <Typography variant="h2">{t(translations.SAVED_SEARCHES)}</Typography>

        <Typography variant="body1">{t(translations.YOU_CAN_RUN_ANY_SAVED_PARAMS)}</Typography>

        <Box display="flex" alignItems="center" padding="10px 0" ml="-13px">
          <Button ref={deleteButtonRef} id="delete-button" onClick={handleDeleteClick} startIcon={<DeleteIcon color="primary" />}>
            <Typography variant="body1" color="primary">
              {t(translations.MENU_DELETE)}
            </Typography>
          </Button>
        </Box>
      </Box>
      <TableHead>
        <TableRow>
          <TableCell
            sx={{
              padding: 0,
              borderRight: '1px solid rgba(224, 224, 224, 1)',
              minWidth: '20px',
            }}
            width="20px"
          >
            <Checkbox
              checked={!!selectedIds?.length && selectedIds?.length === savedSearches?.length}
              onChange={e => toggleSelectAll()}
            />
          </TableCell>
          <TableCell
            width="270px"
            sx={{
              padding: '10px 20px',
              minWidth: '270px',
              maxWidth: '270px',
            }}
          >
            <Typography variant="caption">{t(translations.SAVED_SEARCHES_NAME)}</Typography>
          </TableCell>
          <TableCell
            width="60px"
            sx={{
              padding: '10px 12px',
              borderRight: '1px solid rgba(224, 224, 224, 1)',
              minWidth: '60px',
              maxWidth: '60px',
            }}
          >
            <Typography variant="caption">{t(translations.SAVED_SEARCHES_NEW)}</Typography>
          </TableCell>

          <TableCell
            width="200px"
            sx={{
              padding: '10px 20px',
              minWidth: '200px',
              borderRight: '1px solid rgba(224, 224, 224, 1)',
            }}
          >
            <Typography variant="caption">{t(translations.SAVED_SEARCHES_NOTIFICATION)}</Typography>
          </TableCell>

          <TableCell width="21.5%" sx={{ padding: '10px' }}>
            <Typography variant="caption">{t(translations.SAVED_SEARCHES_SEARCH)}</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <Box maxHeight="310px" overflow="auto">
        <Table>
          <TableBody>
            {savedSearches?.map(savedSearch => (
              <TableRow key={savedSearch.id}>
                <TableCell
                  sx={{
                    padding: 0,
                    minWidth: '20px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  width="20px"
                >
                  <Checkbox checked={selectedIds.includes(savedSearch.id)} onChange={() => toggleSelectItem(savedSearch.id)} />
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  width="270px"
                  sx={{
                    padding: '10px 20px',

                    minWidth: '270px',
                    maxWidth: '270px',
                  }}
                >
                  <Typography variant="caption">
                    <CssTruncation>{savedSearch.name}</CssTruncation>
                  </Typography>
                </TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  width="60px"
                  sx={{
                    padding: '10px 12px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    minWidth: '60px',
                    maxWidth: '60px',
                  }}
                >
                  {savedSearch.newItemsCount > 0 && (
                    <NewCountBadge overlap="circular" color="secondary" badgeContent={savedSearch.newItemsCount} />
                  )}
                </TableCell>
                <TableCell
                  width="200px"
                  sx={{
                    padding: '10px 20px',
                    minWidth: '200px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box flexGrow={1}>
                      <FormControl fullWidth disabled={!savedSearch.alertsEnabled}>
                        <Select
                          labelId="alert-select-label"
                          value={savedSearch.alertsFrequency ?? SavedSearchAlertsFrequency.NO_NOTIFICATION}
                          label=""
                          onChange={e =>
                            updateSavedSearchByName({
                              ...savedSearch,
                              alertsFrequency: e.target.value as SavedSearchAlertsFrequency,
                            })
                          }
                        >
                          <MenuItem value={SavedSearchAlertsFrequency.DAILY}>{t(translations.SEARCH_MENU_DAILY)}</MenuItem>
                          <MenuItem value={SavedSearchAlertsFrequency.WEEKLY}>{t(translations.SEARCH_MENU_WEEKLY)}</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    <Switch
                      checked={savedSearch.alertsEnabled}
                      onChange={e =>
                        updateSavedSearchByName({
                          ...savedSearch,
                          alertsEnabled: e.target.checked,
                        })
                      }
                    />
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    padding: '10px 20px 10px 0px',
                  }}
                >
                  <Button type="text" href="" onClick={() => handleRunSearch(savedSearch)}>
                    <Typography variant="caption" color="primary">
                      {t(translations.SAVED_SEARCHES_RUN)} ▸
                    </Typography>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="end" padding="16px 0">
        <Button variant="outlined" onClick={close}>
          {t(translations.COMMON_CLOSE)}
        </Button>
      </Box>
      {saveSearchNoticeAnchor && saveSearchNoticeAnchor.anchor && (
        <NoticePopover
          id="save_search_notice_popover"
          anchor={saveSearchNoticeAnchor!.anchor}
          onClose={() => setSaveSearchNoticeAnchor(null)}
          title={t(translations.NO_ITEM_SELECTED)}
          text={t(saveSearchNoticeAnchor?.text as string)}
        />
      )}
      {showDeleteConfirmation && (
        <ConfirmPopup
          id="delete-confirmation-popup"
          isOpen
          title={t(translations.DELETE_SAVED_SEARCH)}
          text={
            selectedIds.length > 1 ? t(translations.DELETE_MULTIPLE_SAVED_SEARCHES) : t(translations.DELETE_SINGLE_SAVED_SEARCH)
          }
          cancelBtnText={t(translations.CANCEL_BUTTON)}
          confirmBtnText={t(translations.DELETE_BUTTON)}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  )
}

export default SavedSearchesOverview
